import { z } from 'zod';
import { formatCsv, normalizeHeader, parseCsv } from './csv.js';

/**
 * What the API and the web app must agree on about a CSV import: which files
 * exist, which columns each has, how big a file may be, the request and the
 * shape of the answer. The template a person downloads and the header the
 * server expects are built from the same list, so they cannot drift apart.
 */

export const IMPORT_ENTITIES = ['periods', 'classes', 'students'] as const;
export type ImportEntity = (typeof IMPORT_ENTITIES)[number];
export const importEntity = z.enum(IMPORT_ENTITIES);

export interface ImportColumn {
  key: string;
  required: boolean;
  /** One plausible value, for the downloadable template. */
  example: string;
}

/** Cells that hold several names or emails separate them with this. */
export const IMPORT_LIST_SEPARATOR = ';';

export const IMPORT_COLUMNS: Record<ImportEntity, readonly ImportColumn[]> = {
  periods: [
    { key: 'name', required: true, example: 'Lesson' },
    { key: 'start_time', required: false, example: '09:30' },
    { key: 'end_time', required: false, example: '10:15' },
  ],
  classes: [
    { key: 'name', required: true, example: 'Level 1' },
    { key: 'periods', required: false, example: 'Worship;Lesson' },
    { key: 'teachers', required: false, example: 'teacher@example.org' },
  ],
  students: [
    { key: 'first_name', required: true, example: 'Alice' },
    { key: 'last_name', required: true, example: 'Anderson' },
    { key: 'date_of_birth', required: false, example: '2015-04-09' },
    { key: 'guardian_name', required: false, example: 'Jo Anderson' },
    { key: 'guardian_contact', required: false, example: 'jo@example.org' },
    { key: 'class', required: false, example: 'Level 1' },
  ],
};

/** The web refuses a bigger file before reading it; the schema is the backstop. */
export const IMPORT_MAX_BYTES = 1_048_576;
export const IMPORT_MAX_ROWS: Record<ImportEntity, number> = {
  periods: 200,
  classes: 200,
  students: 1000,
};
/** Names or emails one list cell may hold. Bounds the problems one row can raise. */
export const IMPORT_MAX_LIST_ITEMS = 50;

export const importRequestSchema = z.object({
  csv: z.string().max(IMPORT_MAX_BYTES, 'must be 1 MiB or smaller'),
  /** False previews: the same checks, nothing written. */
  commit: z.boolean().default(false),
});
export type ImportRequest = z.infer<typeof importRequestSchema>;

export interface ImportProblem {
  /** Physical line in the file, the header being line 1. */
  line: number;
  message: string;
}

interface ImportOutcome {
  /** True only when a commit was asked for, nothing was wrong, and the write happened. */
  committed: boolean;
  /** Sorted by line. Any problem means nothing was, or would be, written and every count is 0. */
  problems: ImportProblem[];
  /** Records inserted, or that a preview would insert. */
  created: number;
  /** Rows matched to a record that was already there. */
  existing: number;
}

export interface PeriodImportResult extends ImportOutcome {
  entity: 'periods';
}
export interface ClassImportResult extends ImportOutcome {
  entity: 'classes';
  /** class_periods rows added. Links already present are not counted. */
  attached: number;
  /** class_teachers rows added. */
  assigned: number;
}
export interface StudentImportResult extends ImportOutcome {
  entity: 'students';
  /** Open enrolments added. */
  enrolled: number;
}
export type ImportResult = PeriodImportResult | ClassImportResult | StudentImportResult;

/** The stored form of a name: trimmed, inner whitespace collapsed. */
export const tidy = (value: string): string => value.trim().replaceAll(/\s+/g, ' ');
/** The matching form: tidy and lowercased, so `level 1` finds `Level 1`. */
export const fold = (value: string): string => tidy(value).toLowerCase();

/** Splits a list cell, tidies each item, drops empties and repeats (keeping the first spelling). */
export function splitList(cell: string): string[] {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const part of cell.split(IMPORT_LIST_SEPARATOR)) {
    const item = tidy(part);
    if (item === '') continue;
    const key = fold(item);
    if (seen.has(key)) continue;
    seen.add(key);
    items.push(item);
  }
  return items;
}

export interface ImportRow {
  line: number;
  /** Every column key, values trimmed. Columns the header lacks are ''. */
  cells: Record<string, string>;
}

/** `a`, `a and b`, `a, b and c`: the shape of every list in a sentence here. */
export const joinWords = (words: string[]): string =>
  words.length <= 1 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} and ${words.at(-1)}`;

const lineOf = (text: string, at: number): number => text.slice(0, at).split(/\r\n|\r|\n/).length;

/**
 * Reads a file into rows keyed by column. A problem with the header returns no
 * rows: there is nothing sound to map them onto. A problem with a single row
 * drops that row and keeps the rest, so one round trip reports everything.
 *
 * A column the file does not have is refused rather than skipped. Skipping it
 * would let a header that says `classes` where `class` was meant import every
 * student with no class and a clean preview, and nobody would learn why until
 * the roll was found empty.
 */
export function readImportRows(
  entity: ImportEntity,
  csv: string,
): { rows: ImportRow[]; problems: ImportProblem[] } {
  const columns = IMPORT_COLUMNS[entity];
  const problems: ImportProblem[] = [];

  // A UTF-16 file saved without a byte order mark decodes as UTF-8 into every
  // other byte NUL, which Postgres refuses in a text column. Better one
  // sentence here than a 500 after a clean-looking preview.
  const binary = csv.indexOf('\u0000');
  if (binary !== -1) {
    problems.push({
      line: lineOf(csv, binary),
      message: 'The file is not plain text. Save it as CSV UTF-8 and try again.',
    });
    return { rows: [], problems };
  }

  // File.text() decodes as UTF-8 whatever the file was, so a Latin-1 export
  // arrives with U+FFFD where its accents were. Storing that would put garbled
  // names on a register.
  const undecoded = csv.indexOf('\uFFFD');
  if (undecoded !== -1) {
    problems.push({
      line: lineOf(csv, undecoded),
      message: 'Some characters could not be read. Save the file as CSV UTF-8 and try again.',
    });
    return { rows: [], problems };
  }

  const parsed = parseCsv(csv);
  const [header, ...data] = parsed.records;
  if (!header) {
    // A quote opened on the first line and never closed swallows the whole
    // file. That, not emptiness, is then the thing to fix.
    problems.push(...parsed.problems);
    if (problems.length === 0) problems.push({ line: 1, message: 'The file is empty.' });
    return { rows: [], problems };
  }

  const names = header.fields.map(normalizeHeader);
  const known = new Set(columns.map((column) => column.key));
  const seen = new Set<string>();
  const reported = new Set<string>();
  const unknown: string[] = [];
  names.forEach((name, index) => {
    // A blank cell is a trailing separator, which spreadsheets add, not a column.
    if (name === '') return;
    if (!known.has(name)) {
      unknown.push(header.fields[index]!.trim());
      return;
    }
    if (seen.has(name) && !reported.has(name)) {
      reported.add(name);
      problems.push({ line: header.line, message: `The header lists ${name} twice.` });
    }
    seen.add(name);
  });
  if (unknown.length > 0) {
    const cells = joinWords(unknown.map((cell) => `"${cell}"`));
    const verb = unknown.length === 1 ? 'is not a column' : 'are not columns';
    const allowed = joinWords(columns.map((column) => column.key));
    problems.push({
      line: header.line,
      message: `${cells} ${verb} of a ${entity} file. The columns are ${allowed}.`,
    });
  }
  const missing = columns.filter((column) => column.required && !seen.has(column.key));
  if (missing.length > 0) {
    problems.push({
      line: header.line,
      message: `The header is missing ${joinWords(missing.map((column) => column.key))}.`,
    });
  }
  if (problems.length > 0) {
    // Nothing maps onto a header like this, but what the parser found is
    // still worth fixing in the same pass.
    problems.push(...parsed.problems);
    problems.sort((a, b) => a.line - b.line);
    return { rows: [], problems };
  }

  if (data.length === 0 && parsed.problems.length === 0) {
    problems.push({ line: header.line, message: 'No rows below the header.' });
    return { rows: [], problems };
  }
  const limit = IMPORT_MAX_ROWS[entity];
  if (data.length > limit) {
    problems.push({
      line: 1,
      message: `This file has ${data.length} rows. Import at most ${limit} ${entity} at a time.`,
    });
    return { rows: [], problems };
  }

  problems.push(...parsed.problems);
  const at = new Map(names.map((name, index) => [name, index]));
  const rows: ImportRow[] = [];
  for (const record of data) {
    if (record.fields.length > names.length) {
      // Almost always an unquoted comma in a name, which has shifted every
      // cell after it into the wrong column. A shorter row cannot do that, so
      // it is padded instead.
      problems.push({
        line: record.line,
        message: `Has ${record.fields.length} fields where the header has ${names.length}. Check for an unquoted comma.`,
      });
      continue;
    }
    const cells: Record<string, string> = {};
    for (const column of columns) {
      const index = at.get(column.key);
      cells[column.key] = index === undefined ? '' : (record.fields[index] ?? '').trim();
    }
    rows.push({ line: record.line, cells });
  }
  problems.sort((a, b) => a.line - b.line);
  return { rows, problems };
}

/** The header row and one example row, for the download link on the import panel. */
export function templateCsv(entity: ImportEntity): string {
  const columns = IMPORT_COLUMNS[entity];
  return formatCsv([columns.map((column) => column.key), columns.map((column) => column.example)]);
}
